import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useFormikContext } from 'formik'

/**
 * Renders nothing. Lives inside a Formik <Form> so it can read the form context.
 * On each failed submit attempt (detected via submitCount changing), it finds the
 * first invalid field in the provided visual order, switches to that field's tab,
 * then scrolls it into view and focuses it.
 */
const SubmitErrorNavigator = ({ fieldOrder, activeTab, setActiveTab }) => {
  const { errors, submitCount, values, validateForm } = useFormikContext()
  const handledSubmitCountRef = useRef(submitCount)

  // The game form is validateOnChange:false, so after a failed submit a stale error would
  // linger even once the user fixes the field. Once a submit has been attempted, re-validate
  // as values change so corrected fields clear their error live. (Nothing validates before the
  // first submit, so no eager errors on a fresh form.)
  // Deps are values + submitCount only (validateForm identity is intentionally excluded to
  // avoid a re-validate loop); exhaustive-deps isn't enforced in this repo's eslint config.
  useEffect(() => {
    if (submitCount > 0) validateForm()
  }, [values, submitCount])

  useEffect(() => {
    // Act once per new submit attempt. Formik bumps `submitCount` BEFORE its async
    // validation writes `errors`, so this effect also depends on `errors`: on the submit
    // render `errors` is still stale/empty (find returns nothing → we do NOT mark handled),
    // then when validation resolves and `errors` updates, the effect re-runs with the same
    // (still-unhandled) submitCount and finally navigates.
    if (!submitCount || submitCount === handledSubmitCountRef.current) return

    const first = fieldOrder.find(({ field }) => errors[field])
    if (!first) return

    handledSubmitCountRef.current = submitCount

    if (first.tab !== activeTab) {
      setActiveTab(first.tab)
    }

    // Let the (possibly newly-shown) tab render before touching the DOM.
    setTimeout(() => {
      const el = document.querySelector(`[name="${first.field}"]`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (typeof el.focus === 'function') el.focus()
    }, 0)
  }, [submitCount, errors, fieldOrder, activeTab, setActiveTab])

  return null
}

SubmitErrorNavigator.propTypes = {
  fieldOrder: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      tab: PropTypes.string.isRequired,
    }),
  ),
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
}

SubmitErrorNavigator.defaultProps = {
  fieldOrder: [],
}

export default SubmitErrorNavigator
